// Settings → Deliveries: the reference's "Delivery addresses" card (the
// Settings DELIVERIES tab in Dens N Dente Tailwind.dc.html). It lists every
// address-book line on the NetSuite record — `state.user.info` only carries the
// default one. Read-only for now: the reference's "Add address" form and
// "Set as default" link aren't built, and neither is its "Open for delivery" card.
import { useAuth0 } from "@auth0/auth0-react";
import { useEffect, useState } from "react";

import api from "api/api";
import endpoint from "api/endpoints";
import { ErrorMessage } from "common";

import SettingsHelpLine from "./SettingsHelpLine";

import { Icon, Skeleton } from "@/design-system";

const formatAddress = (a) =>
  [a.addr1, a.addr2, a.city, [a.state, a.zip].filter(Boolean).join(" ")]
    .filter(Boolean)
    .join(", ");

export default function DeliveryAddressesCard({ fallbackName }) {
  const { getAccessTokenSilently } = useAuth0();
  const [addresses, setAddresses] = useState(null); // null while loading
  const [error, setError] = useState("");

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const token = await getAccessTokenSilently();
        const res = await api.get(endpoint.GET_MY_ADDRESSES(), {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!cancelled) setAddresses(Array.isArray(res.data) ? res.data : []);
      } catch {
        if (cancelled) return;
        setError("We couldn't load your addresses. Please try again later.");
        setAddresses([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [getAccessTokenSilently]);

  // The default delivery address first, the way checkout preselects it.
  const sorted = (addresses || [])
    .slice()
    .sort((a, b) => Number(b.isDefaultShipping) - Number(a.isDefaultShipping));

  return (
    <section className="bg-card border border-line-subtle [box-shadow:var(--shadow-xs)] rounded-xl px-[var(--space-6)] py-[var(--space-5)]">
      <div className="flex items-center gap-[14px] mb-[var(--space-4)]">
        <span className="w-[38px] h-[38px] rounded-full bg-blue-50 inline-flex items-center justify-center shrink-0">
          <Icon name="location_on" size={19} className="text-blue-700" />
        </span>
        <div className="min-w-0">
          <h3 className="font-bold text-md leading-tight m-0">
            Delivery addresses
          </h3>
          <p className="text-sm text-ink-3 mt-[2px] mb-0">
            Your default is preselected at checkout — you can still pick another
            per order.
          </p>
        </div>
      </div>

      <div className="flex flex-col gap-[var(--space-3)]">
        {addresses === null ? (
          [0, 1].map((i) => (
            <Skeleton key={i} className="h-[66px] rounded-lg" />
          ))
        ) : error ? (
          <ErrorMessage message={error} />
        ) : sorted.length === 0 ? (
          <div className="flex items-center gap-[14px] border-[1.5px] border-dashed border-line rounded-lg py-[14px] px-[14px] bg-neutral-50">
            <span className="w-[40px] h-[40px] rounded-full bg-card border border-line-subtle inline-flex items-center justify-center shrink-0">
              <Icon
                name="wrong_location"
                size={20}
                className="text-warning-600"
              />
            </span>
            <div className="flex-1">
              <div className="font-semibold text-sm">
                No delivery address yet
              </div>
              <div className="text-sm text-ink-2">
                Add one at checkout — we&apos;ll use it for your orders.
              </div>
            </div>
          </div>
        ) : (
          sorted.map((a) => (
            <div
              key={a.id}
              className="flex items-center gap-[14px] border-[1.5px] border-line-subtle rounded-lg py-[12px] px-[14px] bg-card"
            >
              <span
                className={`w-[40px] h-[40px] rounded-full inline-flex items-center justify-center shrink-0 ${
                  a.isDefaultShipping
                    ? "bg-blue-100 text-blue-700"
                    : "bg-neutral-100 text-ink-3"
                }`}
              >
                <Icon name="location_on" size={20} />
              </span>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-[8px]">
                  <span className="font-semibold text-sm">
                    {a.addressee || a.label || fallbackName || "Address"}
                  </span>
                  {a.isDefaultShipping && (
                    <span className="text-2xs font-bold uppercase tracking-[var(--tracking-wider)] bg-blue-50 text-blue-700 rounded-full py-[2px] px-[8px]">
                      Default
                    </span>
                  )}
                </div>
                <div className="text-sm text-ink-2">{formatAddress(a)}</div>
              </div>
            </div>
          ))
        )}
      </div>

      <SettingsHelpLine className="mt-[var(--space-5)] pt-[var(--space-4)] border-t border-line-subtle">
        Need an address added or changed? We&apos;ll update it the same day.
      </SettingsHelpLine>
    </section>
  );
}
